import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import "./StudentPortal.css";

import { API_BASE_URL } from "../config/apiBase";

const API = API_BASE_URL;

function formatTime(value) {
  if (!value) return "";
  const raw = String(value);
  const normalized = raw.includes(" ") && !raw.includes("T") ? `${raw.replace(" ", "T")}Z` : raw;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
}

function lessonMeta(note) {
  const parts = [];
  if (note?.week !== null && note?.week !== undefined && note?.week !== "") parts.push(`Week ${note.week}`);
  if (note?.lesson !== null && note?.lesson !== undefined && note?.lesson !== "") parts.push(`Lesson ${note.lesson}`);
  return parts.join(" · ");
}

function noteIsActive(note) {
  if (!note || typeof note !== "object") return false;
  const text = String(note.note || "").trim();
  if (!text) return false;
  const status = String(note.status || "").trim().toLowerCase();
  if (status === "draft" || status === "deleted") return false;
  return true;
}

function noteLessonId(note) {
  const lessonId = String(note?.lesson_id || "").trim().toUpperCase();
  if (lessonId) return lessonId;
  if (
    note?.week === null ||
    note?.week === undefined ||
    note?.week === "" ||
    note?.lesson === null ||
    note?.lesson === undefined ||
    note?.lesson === ""
  ) {
    return "";
  }
  const week = Number(note.week);
  const lesson = Number(note.lesson);
  return Number.isFinite(week) && Number.isFinite(lesson) ? `W${week}L${lesson}` : "";
}

function noteDedupeKey(note) {
  const id = Number(note?.id);
  if (Number.isFinite(id)) return `id:${id}`;
  const studentId = String(note?.student_id || "").trim().toUpperCase();
  const lessonId = noteLessonId(note);
  const text = String(note?.note || "").trim();
  const createdAt = String(note?.created_at || "").trim();
  return `${studentId}|${lessonId}|${text}|${createdAt}`;
}

function normalizeTutorNotes(notes, studentId) {
  const sid = String(studentId || "").trim().toUpperCase();
  const seen = new Set();
  const filtered = [];
  for (const note of Array.isArray(notes) ? notes : []) {
    const noteStudentId = String(note?.student_id || "").trim().toUpperCase();
    if (sid && noteStudentId !== sid) continue;
    if (!noteIsActive(note)) continue;
    const key = noteDedupeKey(note);
    if (seen.has(key)) continue;
    seen.add(key);
    filtered.push(note);
  }
  return filtered;
}

export default function StudentTutorNotes({ outletContext } = {}) {
  const params = useParams();
  const navigate = useNavigate();
  const routeOutlet = useOutletContext() || {};
  const outlet = outletContext || routeOutlet;
  const studentId = outlet.studentId || params.id || localStorage.getItem("student_id") || "";
  const closeSidebarPanel = outlet.closeSidebarPanel;
  const sid = useMemo(() => String(studentId || "").toUpperCase(), [studentId]);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const validItems = useMemo(() => normalizeTutorNotes(items, sid), [items, sid]);
  const groupedItems = useMemo(() => {
    const groups = new Map();
    for (const note of validItems) {
      const hasLesson =
        note?.week !== null &&
        note?.week !== undefined &&
        note?.week !== "" &&
        note?.lesson !== null &&
        note?.lesson !== undefined &&
        note?.lesson !== "";
      if (!hasLesson) continue;
      const key = `Week ${note.week} • Lesson ${note.lesson}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(note);
    }
    return Array.from(groups.entries()).map(([label, notes]) => ({ label, notes }));
  }, [validItems]);

  useEffect(() => {
    if (!sid) return;
    let cancelled = false;

    async function loadNotes() {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/student/${sid}/tutor-notes`, {
          cache: "no-store",
        });
        const json = await res.json();
        const list = Array.isArray(json?.notes) ? json.notes : [];
        if (!cancelled) setItems(list);
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadNotes();
    return () => {
      cancelled = true;
    };
  }, [sid]);

  return (
    <section className="sp-content" aria-label="Tutor notes">
      <div className="sp-card">
        <div className="sp-panel-header">
          <h2 className="sp-h2">Tutor Guidance</h2>
          {closeSidebarPanel ? (
            <button
              type="button"
              className="sl-close-btn sp-panel-close-btn"
              onClick={() => closeSidebarPanel()}
              aria-label="Close tutor notes panel"
              title="Close tutor notes panel"
            >
              ×
            </button>
          ) : null}
        </div>
        <p className="sp-text" style={{ marginBottom: 12 }}>
          Latest and previous notes from Shaanzae.
        </p>
        {loading ? (
          <div className="sp-text">Loading…</div>
        ) : groupedItems.length ? (
          <div className="sp-menu-list">
            {groupedItems.map((group) => (
              <div key={group.label} className="sp-menu-item" style={{ display: "block" }}>
                <div className="sp-menu-item-time" style={{ marginBottom: 8, fontWeight: 800 }}>
                  {group.label}
                </div>
                {group.notes.map((n) => {
                  const lessonId = noteLessonId(n);
                  return (
                    <div key={n.id} style={{ padding: "8px 0", borderTop: "1px solid rgba(255,255,255,.08)" }}>
                      <div className="sp-menu-item-time">
                        {lessonMeta(n).replace(" · ", " • ")}
                      </div>
                      <div className="sp-menu-item-msg" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
                        {n.note}
                      </div>
                      <div className="sp-menu-item-time" style={{ marginTop: 6 }}>
                        {formatTime(n.created_at)}
                      </div>
                      <button
                        type="button"
                        className="sp-link"
                        style={{ marginTop: 8 }}
                        onClick={() => navigate(`/student/dashboard/${sid}/dashboard_v2?lesson_id=${encodeURIComponent(lessonId || `W${n.week}L${n.lesson}`)}`)}
                      >
                        Open Lesson
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <div className="sp-text">No tutor guidance has been added yet.</div>
        )}
      </div>
    </section>
  );
}
